import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Form, Input, Checkbox, Button, Tabs, Modal, notification, Select, message } from 'antd';
import axios from 'axios';
import * as _ from 'lodash';
import { connect } from 'react-redux';
import { Icon } from 'dlweb-components';
import TemplateBrowserDrawer from './TemplateBrowserDrawer';
import ProfileProperties from '../../ProfileProperties/ProfileProperties';
import WorkflowForm from '../../WorkflowForm';
import NotificationForm from '../../NotificationForm';
import AdvancedOptionsForm from '../../AdvancedOptionsForm';
import Dropzone from '../../Dropzone';
import { v4 as uuidv4 } from 'uuid';
import useFileUpload from '../../../hooks/useFileUpload';
import profileService from '../../../services/profileService';
import { fileService } from '../../../services/fileService';

const NewDocument = ({angularScope, closeModal,  modalProps, modalOptions, responsiveProps, modalRef, workingFolder, permissions}) => {

  const inputRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [autonameList, setAutonameList] = useState([]);
  const [disabled, setDisabled] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [descriptionInputValue, setDescriptionInputValue] = useState('');
  const [versionNotesInputValue, setVersionNotesInputValue] = useState('');
  const [template, setTemplate] = useState({});
  const [defaultAutoname, setDefaultAutoname] = useState('');
  const [folderSettings, setFolderSettings] = useState({});
  const [autonameDisabled, setAutonameDisabled] = useState(false);
  const [currentPropertyValues, setCurrentPropertyValues] = useState({});
  const [savedInputValue, setSavedInputValue] = useState('');
  const [templates, setTemplates] = useState([]);
  const [hasTemplates, setHasTemplates] = useState(false);

  const [droppedFile, setDroppedFile] = useState(null);

  const [form] = Form.useForm();
  
  const onFileDrop = (files) => {
    if(files.length > 1) {
      return message.error('Multiple files are not allowed. Please drop a single file to use as a template.');
    }
    const file = _.isArray(files) ? files[0] : files;
    setDroppedFile(file);
    onSelect({name: file.name});
  }

  const showLocalFilePicker = async () => {

    if(permissions.allowImport == 0) return;

    try {
      const pickerOptions = {
        multiple: false,
        excludeAcceptAllOption: false
      };
  
      const fileHandle = await fileService.openFileBrowser(pickerOptions);
      const file = await fileHandle.getFile();
      onFileDrop(file);
    }
    catch (err) {
      // do nothing - we get an error when the use cancels the file picker
    }
  }

  const getTemplates = useCallback(async (showAllTemplates) => {
    setLoadingTemplates(true);

		const folderId = showAllTemplates ? "" : workingFolder;

		const templateRes = await axios.post('/api/wt/documents/newdocmodal/templates/read', { folderId: folderId })
    setLoadingTemplates(false);

    return templateRes;

	} , [workingFolder])

    const onTemplateToggle = useCallback(async (showAllTemplates) => {
      const templateRes = await getTemplates(showAllTemplates);
      const { templates, hasTemplates } = templateRes.data.data;

      if(!showAllTemplates) {
        setHasTemplates(hasTemplates);
      }
      setTemplates(templates);
    }, [getTemplates])

  useEffect(() => {
    const setFormValues = async () => {
     
      setLoading(true);

      const res = await axios.get(`/api/wt/documents/autoname/read?folderId=${workingFolder}`);

      const an = res.data.data.map((item) => ({label: item, value: item}));

      setAutonameDisabled(an.length === 1);
      setDefaultAutoname(res.data.data[0]);

      setAutonameList(an);

      const fs = await axios.get(`/api/wt/documents/import/settings/read?folderId=${workingFolder}`);
      setFolderSettings(fs.data.data);

      const { autoNameTemplate } = fs.data.data;

      const hasDefaultTemplate = !!autoNameTemplate;

      form.setFieldValue('useAutoname', hasDefaultTemplate);
      hasDefaultTemplate && form.setFieldValue('newDocumentName', autoNameTemplate);

      const templateRes = await getTemplates(false);

      const { templates, hasTemplates } = templateRes.data.data;

      setHasTemplates(hasTemplates);

      if(hasTemplates) {
        const defaultTemplate = templates.find(t => t.default);

        const dt = {
          id: defaultTemplate.id,
          name: `${defaultTemplate.name}${defaultTemplate.type ? '.' : ''}${defaultTemplate.type}`
        }
        
        setTemplate(dt);
        setDisabled(false);

        if(!hasDefaultTemplate) {
          form.setFieldValue('newDocumentName', dt.name);
        } else {
          setSavedInputValue(dt.name);
        }        
      }

      setTemplates(templates);

      setLoading(false);
    }

    setFormValues();
  }, [form, workingFolder])

  const createNewDocument = useCallback(async (batch) => {

    const done = () => {
      angularScope.$broadcast('reloadGrid');
      closeModal();
      notification.success({ message: `New document created` });
    }

    const controller = new AbortController();
    const signal = controller.signal;
  
    window.currentAbortController = controller;
    window.currentAbortController.batchId = batch.batchId;

    try {
      axios.post('/api/wt/documents/newdocmodal/create', {...batch}, { signal })
      .then(response => {
  
        if (response.status !== 200) {
          notification.error({ message: `Failed to create new document` });
          closeModal();
          return;
        }
  
        if(response.data.errors.length > 0) {
          notification.error({ message: `Failed to create new document` });
          console.error(response.data.errors[0]);
          closeModal();
          return;
        }
  
        const { checkout } = form.getFieldsValue();

        if(checkout) {
          const { documentId } = response.data.data;
          const file = [{ID: documentId}];
          var fileService = angular.element('#container').injector().get('File');
          fileService.download(file, 'checkout', done);
        }
        else {
          done();
        }
      })
      .catch(error => {
        console.error('Error:', error);
      });
    } catch (error) {
      console.error('Error:', error);
    }
  }, [form, angularScope, closeModal])

  const cancelCreate = () => {
    if (window.currentAbortController) {
      window.currentAbortController.abort();
      cancelCleanup(window.currentAbortController.batchId);
      window.currentAbortController = null;
    }
  };

  const submitCreate = useCallback(async (batch) => {
    createNewDocument(batch)
  }, [createNewDocument]);

  const { uploadFiles, pauseUpload, cancelUpload, cancelCleanup, resumeUpload, isUploading } = useFileUpload(submitCreate);

  const handleSubmit = useCallback(async (forms) => {
   
    const { profileName, properties } = currentPropertyValues;
    const { mainForm, notificationForm, workflowForm, advancedOptionsForm } = forms;

		// validate required properties before we call the server
    const validateRequiredProperties = await profileService.validateRequiredProperties(properties);
		if(!validateRequiredProperties) return;

    setProcessing(true);
    setDisabled(true);
    setLoading(true);

    const useAutoname = mainForm.getFieldValue('useAutoname');

    const uploadBatch = {
      batchId: uuidv4(),
      meta: {
        folderId: workingFolder,
        profileName,
        properties,
        autoName: useAutoname ? mainForm.getFieldValue('newDocumentName') : '',
        localTemplateName: '',
        templateId: template.id,
        ...mainForm.getFieldsValue(),
        ...notificationForm.getFieldsValue(),
        ...workflowForm.getFieldsValue(),
        ...advancedOptionsForm.getFieldsValue(),
        newDocumentName: useAutoname ? '' : mainForm.getFieldValue('newDocumentName'),
      }
    }

    profileService.validatePropertyValues(profileName, properties).then(res => {
      const responseMessage = res.data.message;
      if(responseMessage.length > 0) {
        message.error(responseMessage);
        setLoading(false);
        setDisabled(false);
        setProcessing(false);
        return;
      }
      else {
        if(droppedFile) { 
          uploadBatch.files = [droppedFile];
          uploadBatch.meta.localTemplateName = droppedFile.name;
          uploadFiles(uploadBatch);
        }
        else {
          submitCreate({batchId: uploadBatch.batchId, ...uploadBatch.meta});
        }
      }
    })

  }, [droppedFile, workingFolder, currentPropertyValues, template, uploadFiles, submitCreate]);

  const preventInvalidChars = (e) => {
    // allow alphanumeric, backspace, _, ', ., and space
    if (e.key.match(/[^A-Za-z0-9\x08\_\'\. ]/)) {
      e.preventDefault();
    }
  };
  

  const show = () => {
    setOpen(true);
  }

  const browseButton = (
    <div onClick={show} style={{userSelect: 'none', cursor: 'pointer', padding: '0 10px'}}>
      <Icon type='folder' size="lg"/>
    </div>
  )

  const onSelect = (template) => {
    if(!form.getFieldValue('useAutoname')) {
      form.setFieldValue('newDocumentName', template.name);
    } else {
      setSavedInputValue(template.name);
    }

    setDisabled(false);
    setTemplate(template);
    setOpen(false);

    // id indicates the template was selected from the browser
    if(template.id) { 
      setDroppedFile(null);
    }
  }

  const checkFormsForChanges = (name, values, forms) => {

    if(_.indexOf(values[0].name, 'useAutoname') > -1) {
      if(forms.mainForm.getFieldValue('useAutoname')) {
        setSavedInputValue(forms.mainForm.getFieldValue('newDocumentName'));
        form.setFieldValue('newDocumentName', defaultAutoname);
      }
      else {
        form.setFieldValue('newDocumentName', savedInputValue);
      }
    }

    setDisabled(forms.mainForm.getFieldValue('newDocumentName') === '' || _.isEmpty(template));
  }

  const onClose = () => {
    console.log(processing)
    if(processing) {
      pauseUpload();
      Modal.confirm(
        {
          okText: "Yes",
          cancelText: "No",
          title: "Creation in progress",
          content: "Are you sure you want to cancel the new document?",
          onCancel: () => { 
            resumeUpload()
          },
          onOk: () => {
            if(isUploading) {
              cancelUpload();
            }
            else {
              cancelCreate();
            }
            closeModal();
          },
        }
      )
    }
    else {
      closeModal();
    }
  }

  return (
    <Modal
      innerRef={modalRef}
      {...modalOptions}
      {...modalProps}
      {...responsiveProps}
      onCancel={onClose}
    >
      <Form.Provider
        onFormChange={(name, { changedFields, forms }) =>
          checkFormsForChanges(name, changedFields, forms)
        }
        onFormFinish={(name, { forms }) =>
          handleSubmit(forms)
        }
      >
        <Container
          id="new-document-container"
          className="wide-modal-container"
        >
          {processing && <ProcessingOverlay />}
          <Dropzone
            disabled={permissions.allowImport == 0}
            onFileDrop={onFileDrop}
            style={{display: 'unset'}}
          >
          <div className="wide-modal-left">
            <Form
              form={form}
              disabled={loading}
              name="mainForm"
              layout='vertical'
              labelAlign="left"
              initialValues={{description: '', versionNotes: '', checkout: true}}
            >
              <div style={{height: '100%', width: '100%', display: 'flex', flexDirection: 'column', paddingBottom: '10px'}}>
                <Form.Item label="Create new document from" style={{width: '100%', flex: 0}}>
                  <Input
                    readOnly
                    id="template-input"
                    value={template.name}
                    addonAfter={browseButton}
                    onClick={showLocalFilePicker}
                    placeholder={permissions.allowImport == 0 ? "" : "Drag file here or click folder to browse"}
                  />
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate
                >
                  {({ getFieldsValue }) => {
                    const { useAutoname } = getFieldsValue();

                    return (
                      <Form.Item name="newDocumentName" label="New document name" style={{width: '100%', paddingTop: '5px', flex: 0, marginBottom: 0}}>
                        {!useAutoname &&
                          <Input
                            ref={inputRef}
                            value={template.name}
                            onKeyDown={preventInvalidChars}
                          />
                        }
                        {useAutoname &&
                          <Select
                            loading={loading}
                            options={autonameList}
                          />
                        }
                      </Form.Item>
                    );
                  }}
                </Form.Item>

                <Form.Item name="useAutoname" valuePropName="checked" style={{textAlign: 'right', marginTop: 0, marginBottom: '25px'}}>
                    <Checkbox disabled={autonameDisabled}>Autoname</Checkbox>
                </Form.Item>
                <>
                  <Form.Item name="description" label="Description" style={{flex: 1, padding: '0', margin: 0, maxHeight: '300px'}} className="flex-textarea">
                  <Input.TextArea
                    ref={inputRef}
                    maxLength={250}
                    value={descriptionInputValue}
                    onChange={(e) => { setDescriptionInputValue(e.target.value)} }
                    style={{resize: 'none', height: "80%"}}
                  />
                </Form.Item>
                <Form.Item name="versionNotes" label="Version notes" style={{flex: 1, padding: '0', margin: 0, maxHeight: '300px'}} className="flex-textarea">
                  <Input.TextArea
                    ref={inputRef}
                    maxLength={1000}
                    value={versionNotesInputValue}
                    onChange={(e) => { setVersionNotesInputValue(e.target.value)} }
                    style={{resize: 'none', height: "80%"}}
                  />
                </Form.Item>
                <Form.Item name="checkout" valuePropName="checked">
                  <Checkbox style={{marginTop: '5px'}}>Check out document upon completion</Checkbox>
                </Form.Item>
              </>
            </div>
            </Form>
          </div>
          <div className="wide-modal-right">
            {processing &&
              <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10}}>
                <Icon type='circle-notch' size="lg" spin style={{fontSize: '40px', margin: 'auto', display: 'block', color: 'var(--primary)'}}/>
                <div style={{margin: 'auto', marginTop: '5px', fontSize: '22px'}}>Creating document...</div>
              </div>
            }
            <Tabs
              type="card"
              animated={false}
              defaultActiveKey="1"
              destroyInactiveTabPane={false}
              tabBarStyle={{ width: '100%', }}
              style={{height: '100%'}}
              items={[
                {
                  label: <div>
                            <Icon type='list' size="lg" style={{marginRight: '10px'}}/>
                              Profile
                          </div>,
                  key: '1',
                  children: (
                    <div style={{height: '100%'}}>
                      <ProfileProperties workingFolder={workingFolder} setCurrentValues={setCurrentPropertyValues} />
                    </div>
                  ),
                },
                {
                  label: <div>
                            <Icon type='envelope' size="lg" style={{marginRight: '10px'}}/>
                              Notifications
                          </div>,
                  key: '2',
                  forceRender: true,
                  children: <NotificationForm />,
                },
                {
                  label: <div>
                            <Icon type='cogs' size="lg" style={{marginRight: '10px'}}/>
                              Workflow
                          </div>,
                  key: '3',
                  forceRender: true,
                  children: <WorkflowForm workingFolder={workingFolder} folderSettings={folderSettings} />,
                },
                {
                  label: <div>
                            <Icon type='sliders-h' size="lg" style={{marginRight: '10px'}}/>
                              Advanced
                          </div>,
                  key: '4',
                  forceRender: true,
                  children: <AdvancedOptionsForm workingFolder={workingFolder} folderSettings={folderSettings} />,
                }
              ]}
            />
          </div>
          </Dropzone>
        </Container>
        <ButtonContainer>
          <Button
            type="primary"
            form="mainForm"
            htmlType="submit"
            loading={processing}
            disabled={disabled}  
            style={{ float: 'right', marginLeft: '10px' }}
          >
            Create Document
          </Button>
          <Button
            style={{ float: 'right' }}
            onClick={onClose}
            >
            Cancel
          </Button>
        </ButtonContainer>
        <TemplateBrowserDrawer
          open={open}
          onClose={() => setOpen(false)}
          onSelect={onSelect}
          workingFolder={workingFolder}
          permissions={permissions}
          onFileDrop={onFileDrop}
          templates={templates}
          hasTemplates={hasTemplates}
          onTemplateToggle={onTemplateToggle}
          loadingTemplates={loadingTemplates}
        />
      </Form.Provider>
    </Modal>
  );
};

const Container = styled.div`
  .ant-tabs-nav {
    margin-bottom: 0;
  }
  .ant-tabs-tab:first-child {
    border-left: 1px solid #f0f0f0 !important;
  }
  .ant-form-item {
    margin: 5px 0;
  }
  .ant-input-group-addon {
    padding: 0;
  }
  .ant-checkbox + span {
    padding-right: 0px;
  }
  .ant-drawer-header {
    padding: 12px 12px;
    border-bottom: none;
  }
  .ant-drawer-body {
    overflow: hidden;
  }
  .ant-table-thead > tr > th {
    background-color: #fff;
    border-top: 1px solid #f0f0f0;
  }
  .ant-form {
    height: 100%;
  }
  .ant-tabs-content-holder {
    padding: 15px;
  }
  .ant-input-group-addon {
    background-color: #fff;
  }
  .flex-textarea .ant-row {
    display: block;
    height: 100%;
  }
  .flex-textarea .ant-row div:nth-child(2) {
    height: calc(100% - 10px);
  }
  .flex-textarea .ant-row div:nth-child(2) div {
    height: 100%;
  }
  #template-input {
    cursor: default;
  }
`

const ProcessingOverlay = styled.div`
  height: 100%;
  width: 100%;
  background-color: #fff;
  opacity: .5;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 9;
`

const ButtonContainer = styled.div`
  position: absolute;
  bottom: 20px;
  right: 24px;
`
const mapStateToProps = state => {
  const { permissions } = state.workingFolder;
  return { permissions };
};

export default connect(mapStateToProps)(NewDocument);
